import { hasActionPermission } from "./permission-client";
import { canSeeCompanyNav, type CompanyNavId } from "./nav-math";

export type CommandItem = {
  id: string;
  label: string;
  hint?: string;
  /** Extra search tokens (add, so, …) — plan/35 F+ */
  keywords?: string[];
  group: "Recent" | "Results" | "Go" | "Create";
  path: string;
};

/** Desk ⌘K catalog — plan/36 */
export function buildCommandItems(
  companySlug: string,
  held: readonly string[],
): CommandItem[] {
  const go: {
    id: CompanyNavId | string;
    label: string;
    path: string;
    keywords?: string[];
  }[] = [
    {
      id: "home",
      label: "Company home",
      path: `/c/${companySlug}`,
      keywords: ["home", "start"],
    },
    {
      id: "pos",
      label: "POS sales",
      path: `/c/${companySlug}/pos/tickets`,
      keywords: ["pos", "tickets", "register", "floor"],
    },
    {
      id: "pos-terminals",
      label: "POS terminals",
      path: `/c/${companySlug}/pos`,
      keywords: ["pos", "terminals", "devices"],
    },
    {
      id: "sales",
      label: "Sales orders",
      path: `/c/${companySlug}/sales/orders`,
      keywords: ["so", "sales", "orders"],
    },
    {
      id: "contacts",
      label: "Contacts",
      path: `/c/${companySlug}/contacts`,
      keywords: ["customers", "suppliers", "parties", "vendors", "contacts"],
    },
    {
      id: "sales-collect",
      label: "To collect",
      path: `/c/${companySlug}/sales/to-collect`,
      keywords: ["collect", "ar", "receivable", "queue"],
    },
    {
      id: "purchasing",
      label: "Purchase orders",
      path: `/c/${companySlug}/purchasing/orders`,
      keywords: ["po", "purchase", "orders"],
    },
    {
      id: "products",
      label: "Products",
      path: `/c/${companySlug}/products`,
      keywords: ["products", "sku", "catalog"],
    },
    {
      id: "inventory",
      label: "Inventory",
      path: `/c/${companySlug}/inventory`,
      keywords: ["stock", "onhand", "inventory"],
    },
    {
      id: "returns",
      label: "Returns",
      path: `/c/${companySlug}/returns`,
      keywords: ["returns", "refund"],
    },
    {
      id: "trialBalance",
      label: "Trial balance",
      path: `/c/${companySlug}/gl/trial-balance`,
      keywords: ["tb", "gl", "books", "ledger"],
    },
    {
      id: "inbox",
      label: "Inbox",
      path: `/c/${companySlug}/inbox`,
      keywords: ["inbox", "mentions", "messages"],
    },
    {
      id: "approvals",
      label: "Approvals",
      path: `/c/${companySlug}/approvals`,
      keywords: ["approvals", "override", "otp"],
    },
    {
      id: "orgSettings",
      label: "Org settings",
      path: `/c/${companySlug}/settings/org`,
      keywords: ["settings", "org", "company"],
    },
  ];

  const items: CommandItem[] = [];

  for (const g of go) {
    if (g.id === "home") {
      items.push({
        id: "go-home",
        label: g.label,
        group: "Go",
        path: g.path,
        keywords: g.keywords,
      });
      continue;
    }
    if (g.id === "pos-terminals") {
      if (!canSeeCompanyNav(held, "pos")) continue;
      items.push({
        id: "go-pos-terminals",
        label: g.label,
        group: "Go",
        path: g.path,
        keywords: g.keywords,
      });
      continue;
    }
    if (g.id === "sales-collect") {
      if (!canSeeCompanyNav(held, "sales")) continue;
      items.push({
        id: "go-to-collect",
        label: g.label,
        group: "Go",
        path: g.path,
        keywords: g.keywords,
      });
      continue;
    }
    if (!canSeeCompanyNav(held, g.id as CompanyNavId)) continue;
    items.push({
      id: `go-${g.id}`,
      label: g.label,
      group: "Go",
      path: g.path,
      keywords: g.keywords,
    });
  }

  if (
    hasActionPermission(held, "*") ||
    hasActionPermission(held, "inventory.write")
  ) {
    items.push({
      id: "create-product",
      label: "New product",
      hint: "Create",
      group: "Create",
      path: `/c/${companySlug}/products/new`,
      keywords: ["add", "new", "create", "product", "sku", "item"],
    });
  }
  if (
    hasActionPermission(held, "*") ||
    hasActionPermission(held, "sales.write")
  ) {
    items.push({
      id: "create-so",
      label: "New sales order",
      hint: "Create",
      group: "Create",
      path: `/c/${companySlug}/sales/orders/new`,
      keywords: ["add", "new", "create", "so", "sales", "order"],
    });
  }
  if (
    hasActionPermission(held, "*") ||
    hasActionPermission(held, "purchasing.write")
  ) {
    items.push({
      id: "create-po",
      label: "New purchase order",
      hint: "Create",
      group: "Create",
      path: `/c/${companySlug}/purchasing/orders/new`,
      keywords: ["add", "new", "create", "po", "purchase", "order", "buy"],
    });
  }
  if (
    hasActionPermission(held, "*") ||
    hasActionPermission(held, "pos.return") ||
    hasActionPermission(held, "sales.write")
  ) {
    items.push({
      id: "create-return",
      label: "New return",
      hint: "Create",
      group: "Create",
      path: `/c/${companySlug}/returns/new`,
      keywords: ["add", "new", "create", "return", "refund"],
    });
  }

  return items;
}

/** Match when every whitespace token appears in label/hint/group/keywords. */
export function filterCommandItems(
  items: readonly CommandItem[],
  query: string,
): CommandItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...items];
  const tokens = q.split(/\s+/).filter(Boolean);
  return items.filter((item) => {
    const hay = [
      item.label,
      item.hint ?? "",
      item.group,
      ...(item.keywords ?? []),
    ]
      .join(" ")
      .toLowerCase();
    return tokens.every((t) => hay.includes(t));
  });
}
